import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CurrentUser, SessionAccount } from '@xfile/shared';
import { getActiveSlot, setActiveSlot, subscribeActiveSlot } from '../lib/activeSlot';
import { recordAction, resetBuffers } from '../diagnostics/buffers';
import { fetchAccounts, fetchMe, login as apiLogin, logout as apiLogout } from './auth-api';
import { redeemCommunity as apiRedeemCommunity } from '../community/community-api';
import { accountsRetryDelayMs } from './accounts-retry';

/**
 * Contexte d'authentification MULTI-LOGIN (XFILE-14).
 *
 * Le navigateur empile plusieurs comptes (le « faisceau », côté serveur dans un
 * cookie chiffré) ; ce provider expose le compte ACTIF (slot courant), la liste
 * des comptes empilés, et les actions login/logout/switch/add.
 *
 * Discipline d'états (jamais de déconnexion sur une panne réseau) :
 *  - `me` renvoie 401 → `user = null` (slot sans session → écran de login) ;
 *  - `me`/`accounts` en échec réseau/5xx → `networkError`/`accountsError`
 *    (l'UI propose un « réessayer », PAS un retour au login) ;
 *  - `accounts` OK mais liste vide → déconnexion légitime.
 *
 * Seul un VERDICT DU SERVEUR (liste obtenue, ou 401 sur `me`) peut déconnecter.
 * Un échec de récupération laisse l'état « indéterminé » et se RÉESSAIE tout seul
 * (backoff, cf. `accounts-retry.ts`) : sans quoi une panne transitoire — typiquement
 * la rafale de requêtes produite par la restauration d'onglets au redémarrage du
 * navigateur — fige l'app sur un écran d'erreur jusqu'à une action manuelle.
 */

export interface AuthContextValue {
  /** Profil du compte actif (slot courant), ou `null` si aucun. */
  user: CurrentUser | null;
  /** Slot actif dans cet onglet, ou `null`. */
  activeSlot: number | null;
  /** Comptes actuellement empilés dans ce navigateur. */
  accounts: SessionAccount[];
  /** `me` du slot actif en cours. */
  loading: boolean;
  /** Chargement initial de la liste des comptes. */
  accountsLoading: boolean;
  /** Échec de `GET /accounts` (réseau/5xx). */
  accountsError: boolean;
  /** Échec de `GET /me` sur autre chose qu'un 401. */
  networkError: boolean;
  /** Empile un compte (vrai login) et le rend actif. `stayLoggedIn` = cookie persistant. */
  login: (email: string, password: string, stayLoggedIn: boolean) => Promise<void>;
  /**
   * Échange un jeton d'accès communautaire (XFILE-08) contre une session forcée en
   * lecture seule, empilée et rendue active (comme `login`, sans mot de passe).
   * Propage l'erreur d'échange (`CommunityApiError`) à l'appelant (page `/join`).
   */
  redeemCommunity: (token: string) => Promise<void>;
  /** Déconnecte le compte du `slot` (ou TOUS si `all`). */
  logout: (slot: number, all?: boolean) => Promise<void>;
  /** Bascule le compte actif (sans re-login) et revient à l'accueil. */
  switchTo: (slot: number) => void;
  /** Va à l'écran de login pour empiler un nouveau compte. */
  addAccount: () => void;
  /** Recharge la liste des comptes. */
  refreshAccounts: () => Promise<void>;
  /** Recharge le profil du compte actif. */
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Projette un profil en entrée de compte empilé (mode forcé/tests). */
function accountFromUser(user: CurrentUser, slot: number): SessionAccount {
  return {
    slot,
    id: user.id,
    email: user.email,
    displayName: user.displayName,
    role: user.role,
  };
}

interface AuthProviderProps {
  children: React.ReactNode;
  /**
   * Forçage test : court-circuite les appels réseau (`accounts`/`me`).
   * `CurrentUser` → connecté (slot 0) ; `null` → anonyme ; `undefined` → mode réel.
   */
  initialUser?: CurrentUser | null;
}

export function AuthProvider({ children, initialUser }: AuthProviderProps): React.JSX.Element {
  const forced = initialUser !== undefined;
  const navigate = useNavigate();

  const [activeSlot, setActiveSlotState] = useState<number | null>(() =>
    forced ? (initialUser ? 0 : null) : getActiveSlot(),
  );
  const [user, setUser] = useState<CurrentUser | null>(() => initialUser ?? null);
  const [accounts, setAccounts] = useState<SessionAccount[]>(() =>
    forced && initialUser ? [accountFromUser(initialUser, 0)] : [],
  );
  const [loading, setLoading] = useState<boolean>(!forced);
  const [accountsLoading, setAccountsLoading] = useState<boolean>(!forced);
  const [accountsError, setAccountsError] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  // Génération courante du chargement des comptes : l'incrémenter ANNULE toute
  // boucle de réessai en vol (démontage, ou nouvel appel à `refreshAccounts`).
  const generationRef = useRef(0);
  // Attente de backoff en cours, annulable (le `resolve` est appelé à l'annulation
  // pour que la boucle sorte immédiatement au lieu de rester suspendue).
  const pendingRetryRef = useRef<{ timer: ReturnType<typeof setTimeout>; resolve: () => void } | null>(
    null,
  );

  const cancelPendingRetry = useCallback(() => {
    const pending = pendingRetryRef.current;
    if (!pending) return;
    pendingRetryRef.current = null;
    clearTimeout(pending.timer);
    pending.resolve();
  }, []);

  /** UNE tentative. `true` = verdict du serveur obtenu ; `false` = échec de récupération. */
  const attemptLoadAccounts = useCallback(async (generation: number): Promise<boolean> => {
    setAccountsLoading(true);
    try {
      const list = await fetchAccounts();
      if (generation !== generationRef.current) return true; // tentative périmée
      setAccounts(list);
      setAccountsError(false);
      // Défaut : slot actif absent des comptes → plus petit slot connecté (ne
      // CLOBBER jamais un slot valide non-minimal). C'est CE repositionnement qui
      // retrouve le compte après un redémarrage du navigateur (`sessionStorage`
      // vidé par conception → aucun slot actif au bootstrap).
      const slots = list.map((a) => a.slot).sort((a, b) => a - b);
      const cur = getActiveSlot();
      if (cur === null || !slots.includes(cur)) {
        setActiveSlot(slots.length > 0 ? slots[0]! : null);
      }
      setAccountsLoading(false);
      return true;
    } catch {
      if (generation !== generationRef.current) return true; // tentative périmée
      // ÉCHEC DE RÉCUPÉRATION ≠ DÉCONNEXION : on ne vide NI `accounts` NI le slot.
      setAccountsError(true);
      setAccountsLoading(false);
      return false;
    }
  }, []);

  /** Boucle de réessai, en TÂCHE DE FOND (ne bloque jamais l'appelant de `refreshAccounts`). */
  const retryLoadAccounts = useCallback(
    async (generation: number): Promise<void> => {
      for (let attempt = 0; generation === generationRef.current; attempt += 1) {
        await new Promise<void>((resolve) => {
          pendingRetryRef.current = {
            timer: setTimeout(() => {
              pendingRetryRef.current = null;
              resolve();
            }, accountsRetryDelayMs(attempt)),
            resolve,
          };
        });
        if (generation !== generationRef.current) return;
        if (await attemptLoadAccounts(generation)) return;
      }
    },
    [attemptLoadAccounts],
  );

  const refreshAccounts = useCallback(async () => {
    generationRef.current += 1;
    const generation = generationRef.current;
    cancelPendingRetry();
    // On n'attend QUE la première tentative : les réessais poursuivent en fond, sinon
    // `login()`/`logout()` resteraient suspendus tant que le serveur est injoignable.
    if (!(await attemptLoadAccounts(generation))) void retryLoadAccounts(generation);
  }, [attemptLoadAccounts, cancelPendingRetry, retryLoadAccounts]);

  // Bootstrap + abonnement au store de slot (mode réel uniquement).
  useEffect(() => {
    if (forced) return;
    const unsubscribe = subscribeActiveSlot(() => {
      const slot = getActiveSlot();
      setActiveSlotState(slot);
      // Un slot vient d'être désigné → profil « en cours de résolution ». Sans ce
      // `loading` synchrone, un render intercalé (avant l'effet `me`) verrait
      // user=null + loading=false et redirigerait à tort vers /login.
      if (slot !== null) setLoading(true);
    });

    void refreshAccounts();

    return () => {
      unsubscribe();
      // Démontage : annule la boucle de réessai (sinon elle survit au composant).
      generationRef.current += 1;
      cancelPendingRetry();
    };
  }, [forced, refreshAccounts, cancelPendingRetry]);

  // Profil du slot actif (mode réel uniquement).
  useEffect(() => {
    if (forced) return;
    if (activeSlot === null) {
      setUser(null);
      setLoading(false);
      setNetworkError(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setNetworkError(false);
    fetchMe(activeSlot, controller.signal)
      .then((me) => {
        setUser(me); // `null` (401) = slot sans session → géré comme non authentifié.
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // 5xx / réseau : on NE déconnecte PAS, on signale.
        setNetworkError(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [forced, activeSlot]);

  const login = useCallback(
    async (email: string, password: string, stayLoggedIn: boolean) => {
      const data = await apiLogin(email, password, stayLoggedIn);
      recordAction('auth.login', { slot: data.slot });
      setActiveSlot(data.slot);
      setUser(data.user);
      await refreshAccounts();
    },
    [refreshAccounts],
  );

  const redeemCommunity = useCallback(
    async (token: string) => {
      const data = await apiRedeemCommunity(token);
      recordAction('community.redeem', { slot: data.slot });
      setActiveSlot(data.slot);
      setUser(data.user);
      await refreshAccounts();
    },
    [refreshAccounts],
  );

  const logout = useCallback(
    async (slot: number, all = false) => {
      recordAction('auth.logout', { slot, all: all ? 1 : 0 });
      await apiLogout(slot, all);
      // Déconnexion totale : purge les anneaux de diagnostic (aucune activité d'un
      // compte ne survit dans le rapport d'un autre).
      if (all) resetBuffers();
      await refreshAccounts(); // re-sélectionne le plus petit slot restant (ou null).
    },
    [refreshAccounts],
  );

  const switchTo = useCallback(
    (slot: number) => {
      recordAction('auth.switch', { slot });
      setActiveSlot(slot);
      navigate('/');
    },
    [navigate],
  );

  const addAccount = useCallback(() => {
    recordAction('auth.addAccount');
    navigate('/login');
  }, [navigate]);

  const refreshMe = useCallback(async () => {
    const slot = getActiveSlot();
    if (slot === null) {
      setUser(null);
      return;
    }
    try {
      setUser(await fetchMe(slot));
      setNetworkError(false);
    } catch {
      setNetworkError(true);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      activeSlot,
      accounts,
      loading,
      accountsLoading,
      accountsError,
      networkError,
      login,
      redeemCommunity,
      logout,
      switchTo,
      addAccount,
      refreshAccounts,
      refreshMe,
    }),
    [
      user,
      activeSlot,
      accounts,
      loading,
      accountsLoading,
      accountsError,
      networkError,
      login,
      redeemCommunity,
      logout,
      switchTo,
      addAccount,
      refreshAccounts,
      refreshMe,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Accès au contexte d'authentification. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans un <AuthProvider>');
  return ctx;
}
